import SelfcarePageContent from "./SelfcarePageContent";
import {useCustomer} from "../../layouts/SelfcareLayout";
import {JSX, useEffect, useState} from "react";
import {Hardware} from "../../util/types";
import {selfcareHardware, enableMpro, disableMpro, statusMpro} from "../../util/request";
import {Link} from "react-router-dom";
import LoaderSpinner from "../loaders/LoaderSpinner";
import Routing from "../../Routing.ts";

const MESH_EXTENDER_IDS = [100002];

export default function HardwareOverview() {
    const {customer} = useCustomer()

    const [loading, setLoading] = useState<boolean>(true)
    const [hardware, setHardware] = useState<Hardware[]>([])

    useEffect(() => {
        selfcareHardware(customer.customer_id)
            .then(r => setHardware(r.response.data))
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [customer])

    if (loading) {
        return <LoaderSpinner/>
    }

    if (hardware.length === 0) {
        return <></>
    }

    return (<>
        <SelfcarePageContent className={'selfcare-page-content--wide selfcare-page-hardware'}>
            <div className={'selfcare-page-summary'}>
                <h1 className={'heading heading--big'}>Udstyr</h1>
                <p>Her kan du få et overblik over dit udstyr og dets specifikationer.</p>
            </div>

            <div className="selfcare-hardware">
                {hardware.map((item: Hardware, index: number) => <HardwareItem key={index} item={item} hardware={hardware}/>)}
            </div>
        </SelfcarePageContent>
        <hr/>
    </>)
}

async function waitForMproStatusIsNotPending(customer_id: string, serial: string) {
    const interval = 5000;
    const timeout_limit = 120 * 1000;
    let time_passed = 0;

    return new Promise<CpeMPROStatus>((resolve: (value: CpeMPROStatus) => void, reject) => {
        const mproStatusInterval = setInterval(() => {
            time_passed += interval;
            statusMpro(customer_id, serial).then((result) => {
                if (result.response.data.status !== 'cpe pending') {
                    window.clearInterval(mproStatusInterval);
                    resolve(result.response.data.status);
                }
            });

            if (time_passed >= timeout_limit) {
                window.clearInterval(mproStatusInterval);
                reject();
            }

        }, interval);
    });
}

type CpeMPROStatus = 'enabled' | 'disabled' | 'cpe pending' | 'unknown'

const HardwareItem = ({item, hardware}: {
    item: Hardware,
    hardware: Hardware[]
}) => {
    const {customer} = useCustomer()
    const [loading, setLoading] = useState<boolean>(true)
    const [status, setStatus] = useState<CpeMPROStatus>('unknown')
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const hasExtender = hardware.some((h) => MESH_EXTENDER_IDS.includes(h.hardware_type_id));
    const isExtender = item.hardware_type_id === 100002;
    const canToggleMesh = item.wifi_mesh_compatible && !MESH_EXTENDER_IDS.includes(item.hardware_type_id) && (hasExtender || status === 'enabled');
    const noImageClass = item.image_selfcare ? '' : ' no-image';

    useEffect(() => {
        if (!canToggleMesh) {
            return;
        }
        waitForMproStatusIsNotPending(customer.customer_id, item.serial_number)
            .then((status) => {
                setStatus(status);
                setLoading(false)
            })
            .catch(() => {});
    }, [customer, item, canToggleMesh]);

    return <section className={`selfcare-hardware-item${noImageClass}`}>
        {item.image_selfcare &&
            <img src={item.image_selfcare} className={item.slug} alt={item.hardware_display_name}/>
        }

        <div className="selfcare-hardware-text">
            <h3 className="heading heading--montserrat">
                {item.hardware_display_name}
            </h3>
            {(canToggleMesh || isExtender) &&
                <div className="buttons">
                    {canToggleMesh && (
                        <MeshButton item={item} status={status} loading={loading}></MeshButton>
                    )}
                    {isExtender && (
                        <Link to={Routing.Broadband.Mesh}>
                            Om Wifi Mesh
                        </Link>
                    )}
                </div>
            }
            {item.serial_number || item.wifi_generation || item.wifi_antenna_configuration || item.wifi_standard || item.wifi_frequency_configuration || item.lan_port_configuration || item.wan_port_configuration || item.wifi_mesh_compatible ?
                <div className="specifications-button" onClick={() => setIsOpen(!isOpen)}>
                    <span className="text">Specifikationer</span>
                    <span className={`arrow${isOpen ? " arrow-up" : " arrow-down"}`}></span>
                </div> : <></>
            }
        </div>

        <div className={`selfcare-hardware-specifications${isOpen ? " open" : ""}`}>
            <table className={'selfcare-hardware-specifications-table'}>
                <tbody>
                    {item.serial_number && <tr>
                        <th><p>Serienummer:</p></th>
                        <td><p>{item.serial_number}</p></td>
                    </tr>}
                    {item.hardware_type_id === 100002 && <tr>
                        <th><p>Model:</p></th>
                        <td><p>Zyxel WX3100</p></td>
                    </tr>}
                    {item.wifi_generation && <tr>
                        <th><p>Wifi generation:</p></th>
                        <td><p>Wifi {item.wifi_generation}</p></td>
                    </tr>}
                    {(item.wifi_antenna_configuration || item.wifi_standard) && <tr>
                        <th><p>Antenner:</p></th>
                        <td><p>{item.wifi_antenna_configuration} {item.wifi_standard}-teknologi</p></td>
                    </tr>}
                    {item.wifi_frequency_configuration && <tr>
                        <th><p>Wifi frekvenser:</p></th>
                        <td><p>{item.wifi_frequency_configuration}</p></td>
                    </tr>}
                    {(item.lan_port_configuration || item.wan_port_configuration) && <tr>
                        <th><p>Porte:</p></th>
                        <td>
                            {item.lan_port_configuration && <p>{item.lan_port_configuration} LAN-porte</p>}
                            {item.wan_port_configuration && <p>{item.wan_port_configuration} WAN-port</p>}
                        </td>
                    </tr>}
                    {item.wifi_mesh_compatible && <tr>
                        <th><p>Wifi Mesh kompatibel:</p></th>
                        <td><p>Ja</p></td>
                    </tr>}
                </tbody>
            </table>
        </div>
    </section>
}

const MeshButton = ({item, status, loading}: {
    item: Hardware,
    status: string,
    loading: boolean,
}) => {
    const [popUpOpen, setPopUpOpen] = useState<boolean>(false)
    const [toggleMproStatusRunning, setToggleMproStatusRunning] = useState<boolean>(false);

    const onClick = () => {
        setPopUpOpen(!popUpOpen)
    }

    if (loading) {
        return <LoaderSpinner/>
    }

    return <>
        <button onClick={onClick}>
            Aktivér/deaktivér mesh
        </button>
        {popUpOpen && <MeshPopUpContainer
            item={item}
            toggleMeshPopUp={onClick}
            status={status}
            toggleMproStatusRunning={toggleMproStatusRunning}
            setToggleMproStatusRunning={setToggleMproStatusRunning}
        />}
    </>
}

const MeshPopUpContainer = ({item, toggleMeshPopUp, status, toggleMproStatusRunning, setToggleMproStatusRunning}: {
    item: Hardware,
    toggleMeshPopUp: () => void,
    status: string
    toggleMproStatusRunning: boolean
    setToggleMproStatusRunning: (v: boolean) => void
}) => {
    const {customer} = useCustomer()
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false)
    const errorMessage = (
        <div className="error-box">
            <h4 className="heading heading--montserrat heading--left heading--red">Der skete en fejl</h4>
            <p>
                Prøv igen om 2 minutter.
                Fortsætter problemet, bedes du kontakte os på{" "}
                <a href="tel:+4588772000">88&nbsp;77&nbsp;20&nbsp;00</a> eller <a href="mailto:kundeservice@hiper.dk">kundeservice@hiper.dk</a>.
            </p>
        </div>
    )

    const toggleMproStatus = (status: string | false, serial: string) => {
        setToggleMproStatusRunning(true);
        if (status === 'disabled') {
            waitForMproStatusIsNotPending(customer.customer_id, item.serial_number)
                .then(() => {
                    enableMpro(customer.customer_id, serial)
                        .then(() => {
                            setToggleMproStatusRunning(false)
                            setConfirmed(true);
                        }).catch(() => {
                        setError(true)
                    });
                });

        } else {
            waitForMproStatusIsNotPending(customer.customer_id, item.serial_number)
                .then(() => {
                    disableMpro(customer.customer_id, serial)
                        .then(() => {
                            setToggleMproStatusRunning(false)
                            setConfirmed(true);
                        }).catch(() => {
                        setError(true)
                    });
                });
        }
    }

    if (status === 'enabled') {
        return <MeshPopUp onOverlayClick={() => toggleMeshPopUp()}>
            <h3 className="heading heading--montserrat">Deaktivér mesh på din router</h3>
            <p>Hvis du ikke længere skal bruge mesh, anbefaler vi at du deaktiverer det.</p>
            {confirmed ?
                <div className="action-box">
                    <h4 className="heading heading--montserrat">Vi deaktiverer mesh på din router</h4>
                    <p>Det kan tage op til 2 minutter.</p>
                    <div className="buttons">
                        <button className="cancel" onClick={() => toggleMeshPopUp()}>Luk besked</button>
                    </div>
                </div>
                :
                (error ?
                    errorMessage
                    :
                    <div className="buttons">
                        {toggleMproStatusRunning ? <LoaderSpinner/> :
                            <>
                                <button className="cancel" onClick={() => toggleMeshPopUp()}>Annullér</button>
                                <button
                                    className="confirm"
                                    onClick={() => toggleMproStatus(status, item.serial_number)}
                                >Deaktivér
                                </button>
                            </>
                        }
                    </div>
                )
            }
        </MeshPopUp>
    } else if (status === 'disabled') {
        return <MeshPopUp onOverlayClick={() => toggleMeshPopUp()}>
            <h3 className="heading heading--montserrat">Aktivér mesh på din router</h3>
            <p>Når du trykker <strong>Aktivér</strong>, sker følgende:</p>
            <ul>
                <li>Dine enheder mister kortvarigt wifi-forbindelsen.</li>
                <li>Er dit wifi opdelt i 2,4 og 5&nbsp;GHz, bliver det slået sammen til ét.</li>
                <ul>
                    <li>F.eks. bliver &ldquo;Zyxel_XXXX&rdquo; og &ldquo;Zyxel_XXXX_5G&rdquo; slået sammen til &ldquo;Zyxel_XXXX&rdquo;.</li>
                    <li>Du kan ikke opdele det igen, mens mesh er aktiveret.</li>
                    <li>Enheder på 5&nbsp;GHz skal du selv forbinde til wifi igen.</li>
                </ul>
            </ul>
            <p>
                Når du har aktiveret mesh, kan du installere dine mesh-enheder.
                Find vejledningen <a href="/wifi-mesh-vejledning" target="_blank">her</a>.
            </p>
            {confirmed ?
                <div className="action-box">
                    <h4 className="heading heading--montserrat">Vi aktiverer mesh på din router</h4>
                    <p>Det kan tage op til 2 minutter.</p>
                    <small>
                        Har du allerede forbundet en mesh-enhed?
                        Så anbefaler vi at nulstille og geninstallere den.
                    </small>
                    <div className="buttons">
                        <button className="cancel" onClick={() => toggleMeshPopUp()}>Luk besked</button>
                    </div>
                </div>
                :
                (error ?
                    errorMessage
                    :
                    <div className="buttons">
                        {toggleMproStatusRunning ? <LoaderSpinner/> :
                            <>
                                <button className="cancel" onClick={() => toggleMeshPopUp()}>Annullér</button>
                                <button
                                    className="confirm"
                                    onClick={() => toggleMproStatus(status, item.serial_number)}
                                >Aktivér
                                </button>
                            </>
                        }
                    </div>
                )
            }
        </MeshPopUp>
    }

    return <MeshPopUp onOverlayClick={() => toggleMeshPopUp()}>
        <p>
            Vi kan desværre ikke udføre handlingen, da vi ikke kan kommunikere med din router.
        </p>
        <ul>
            <p>Prøv følgende:</p>
            <li>Tjek om routeren er tændt.</li>
            <li>Tjek om serienummeret bag på routeren er det samme som <code>{item.serial_number}</code>.</li>
            <li>Genindlæs siden og prøv igen.</li>
            <li>Prøv igen senere.</li>
        </ul>
        <p>
            Hvis problemet fortsætter, skal du kontakte os på{" "}
            <a href="tel:+4588772000">88&nbsp;77&nbsp;20&nbsp;00</a> eller <a href="mailto:kundeservice@hiper.dk">kundeservice@hiper.dk</a>.
        </p>

        <div className="action-box">
            <div className="buttons">
                <button className="cancel" onClick={() => toggleMeshPopUp()}>Luk besked</button>
            </div>
        </div>
    </MeshPopUp>
}


const MeshPopUp = ({children, onOverlayClick}: { children: string | JSX.Element | JSX.Element[], onOverlayClick: () => void }) => {
    return <>
        <div className="selfcare-popup-overlay" onClick={onOverlayClick}></div>
        <div className="selfcare-popup selfcare-popup--mesh">
            {children}
        </div>
    </>
}
